import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import { get, isEmpty } from 'lodash';
import gql from 'graphql-tag';
import { graphql, Mutation } from 'react-apollo';
import Button from "@material-ui/core/Button";
import moment from 'moment';

export const SharePlan = gql`
  mutation sharePlan($id: ID!) {
    sharePlan(id: $id)
  }
`;

// Simple component without theming to avoid SuccessionThemeProvider errors
const CompletedStepsContent = ({ 
  card, 
  classes, 
  dealTrackingPipeLineId, 
  enqueueSnackbar, 
  updateCard, 
  pipeline 
}) => {
  const [lastSent, setLastSent] = useState(null);
  const userGoal = get(card, 'primary_agent.continuity_builder.userGoal');
  const agent_id = get(card, 'primary_agent.id');

  if (userGoal && !userGoal.completed) {
    return (
      <div>
        <div>{14 - userGoal.remaining_step_count} out of 14 steps completed</div>
        <AccessDates card={card} pipeline={pipeline} />
      </div>
    );
  }

  if (userGoal && userGoal.completed) {
    const planSent = lastSent || get(card, 'continuity_builder.businessContinuityPlan.planSentAt');
    return (
      <Mutation mutation={SharePlan}>
        {sharePlan => (
          <div>
            <a
              href={`/continuity/download?agent_id=${agent_id})}`}
              style={{ cursor: 'pointer', color: 'rgb(0, 107, 219)' }}>
              Download Continuity Plan
            </a>
            <p>Press this button to share plan activation.</p>
            <Button
              onClick={() => {
                const { id } = card.continuity_builder;
                sharePlan({ variables: { id } }).then(() => {
                  enqueueSnackbar('Plan has been shared.', {
                    variant: 'success',
                    anchorOrigin: {
                      vertical: 'top',
                      horizontal: 'center',
                    },
                  });
                  setLastSent(moment().format('LLLL'));
                });
              }}
              variant="contained"
              color="primary">
              {planSent ? 'Re-Share Plan' : 'Share Plan'}
            </Button>
            {planSent && <div>Last Sent: {moment(planSent).format('LLLL')}</div>}
            <br />
            <AccessDates card={card} pipeline={pipeline} />
          </div>
        )}
      </Mutation>
    );
  }
  return '';
};

const showCompleted = userGoal => {
  if (!userGoal || userGoal.completed) {
    return true;
  }
  return false;
};

const clickViewAll = (dealId, id) => {
  window.open(`/admin/pipelines/${id}?deal_id=${dealId}`, '_self');
};

const AccessDates = ({ card, pipeline }) => {
  return (
    <div>
      {get(card, 'continuity_builder.accessedDate') && (
        <div>Accessed: {moment(get(card, 'continuity_builder.accessedDate')).format('MM-DD-YYYY')}</div>
      )}
      {get(card, 'continuity_builder.lastUpdated') && (
        <div>Last Updated: {moment(get(card, 'continuity_builder.lastUpdated')).format('MM-DD-YYYY')}</div>
      )}
      {!get(card, 'continuity_builder.deal.number_of_deal_interests') && (
        <div style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}>
          <span onClick={() => clickViewAll(get(card, 'continuity_builder.deal.id'), pipeline.id)}>
            Create Deal Room
          </span>
        </div>
      )}
    </div>
  );
};

const steps = [
  {
    label: 'Find Partners',
    content: (card, dealTrackingPipeLineId, props) => {
      const successors = get(card, 'primary_agent.continuity_builder.continuityBuilderSuccessors', []);
      return (
        <div>
          {successors.map(s => (
            <div>
              {`Successor ${s.ordinal} Name: ${get(s, 'inventoryUser.name', '-')}`}
              <br />
              {`Successor ${s.ordinal} Email: ${get(s, 'inventoryUser.email', '-')}`}
              <br />
              <br />
            </div>
          ))}
          {isEmpty(successors) &&
            get(card, 'primary_agent.company.business_type') === 'wealth_rj' &&
            get(card, 'primary_agent.continuity_builder.searchedForPartner') && <div>Searched for a partner</div>}
          {get(card, 'primary_agent.continuity_builder.existingPartnerName', '') && (
            <div>
              {`Partner Name: ${get(card, 'primary_agent.continuity_builder.existingPartnerName', '')}`}
              <br />
              {`Partner Email: ${get(card, 'primary_agent.continuity_builder.existingPartnerEmail')}`}
              <br />
              {`Last Updated: ${moment(get(card, 'primary_agent.continuity_builder.lastUpdated')).format(
                'MM-DD-YYYY'
              )}`}
              <br />
              <br />
            </div>
          )}
          {get(card, 'primary_agent.continuity_builder.deal.number_of_deal_interests', 0) === 0 &&
            successors.length < 1 &&
            !get(card, 'primary_agent.company.disable_deal_room_from_bcb') &&
            get(card, 'primary_agent.company.business_type') !== 'wealth_rj' && <div>Started creating a deal room</div>}
          <AccessDates card={card} />
        </div>
      );
    },
    completed: card => {
      const successors = get(card, 'primary_agent.continuity_builder.continuityBuilderSuccessors', []);
      return (
        get(card, 'primary_agent.continuity_builder.completedFindPartnerStep') ||
        (successors.length > 0 &&
          !get(card, 'primary_agent.company.disable_deal_room_from_bcb') &&
          get(card, 'primary_agent.company.business_type') !== 'wealth_rj') ||
        get(card, 'primary_agent.continuity_builder.existingPartnerName', false) ||
        get(card, 'primary_agent.continuity_builder.searchedForPartner', false) ||
        get(card, 'primary_agent.continuity_builder.deal.number_of_deal_interests', 0) > 0 ||
        get(card, 'primary_agent.continuity_builder.userGoal.completed', false)
      );
    },
  },
  {
    label: 'File Agreement',
    content: (card, dealTrackingPipeLineId, props) => {
      return (
        <div>
          <CompletedStepsContent 
            card={card} 
            dealTrackingPipeLineId={dealTrackingPipeLineId}
            classes={props.classes}
            enqueueSnackbar={props.enqueueSnackbar}
            updateCard={props.updateCard}
            pipeline={props.pipeline}
          />
        </div>
      );
    },
    completed: card => get(card, 'primary_agent.continuity_builder.completedFileAgreementStep'),
  },
];

const CompletedSteps = ({ card, classes, dealTrackingPipeLineId, enqueueSnackbar, updateCard, pipeline }) => {
  const props = { card, classes, dealTrackingPipeLineId, enqueueSnackbar, updateCard, pipeline };
  
  return (
    <div>
      {steps.map((step, index) => {
        const isCompleted = step.completed(card);
        const showContent = showCompleted(get(card, 'primary_agent.continuity_builder.userGoal'));
        
        return (
          <div key={index} style={{ marginBottom: '20px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '8px',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              <span style={{ 
                color: isCompleted ? '#10b981' : '#6b7280',
                marginRight: '8px'
              }}>
                {isCompleted ? '✓' : '○'}
              </span>
              {step.label}
            </div>
            {showContent && (
              <div style={{ marginLeft: '24px' }}>
                {step.content(card, dealTrackingPipeLineId, props)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

CompletedSteps.propTypes = {
  card: PropTypes.object.isRequired,
  classes: PropTypes.object,
  dealTrackingPipeLineId: PropTypes.string,
  enqueueSnackbar: PropTypes.func,
  updateCard: PropTypes.func,
  pipeline: PropTypes.object,
};

export default withSnackbar(CompletedSteps);
